/**
 * The @mention picker for the comment composer. Anchored with MUI `Popper`, so
 * it flips above the input when it would overflow, which the docked panel's
 * footer composer depends on.
 */
import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Popper from "@mui/material/Popper";
import { useUserMentions } from "context/UserMentionProvider";
import { fullName } from "utils/fullName";
import { CommentUser } from "./types";
import UserAvatar from "components/common/users/UserAvatar";
import { Inline } from "components/layout/reusableLayouts";

export interface MentionsDropdownProps {
  open: boolean;
  query: string;
  /** Element to anchor against. Popper measures this for flip + offset. */
  anchorEl: HTMLElement | null;
  onSelect: (user: CommentUser) => void;
  onClose?: () => void;
}

const MAX_DROPDOWN_RESULTS = 15;

const highlightMatch = (text: string, query: string): React.ReactNode => {
  const trimmed = query.trim();
  if (!trimmed) return text;
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Capturing-group split keeps the match in the result array; matched
  // segments land at odd indices. We highlight by index instead of
  // re-testing each part, since a `/g` regex's `.test()` is stateful and
  // would mis-flag/skip segments on repeated calls.
  const parts = text.split(new RegExp(`(${escaped})`, "i"));
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <Box
        component="span"
        key={i}
        sx={{
          color: "var(--color-text-brand)",
          fontWeight: "var(--type-weight-semibold)",
        }}
      >
        {part}
      </Box>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );
};

const MentionsDropdown: React.FC<MentionsDropdownProps> = ({
  open,
  query,
  anchorEl,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation();
  const { users, loading: usersLoading } = useUserMentions();
  const [activeIndex, setActiveIndex] = useState(0);
  const [anchorWidth, setAnchorWidth] = useState<number | undefined>(undefined);

  // Track the anchor's width through layout changes (viewport resize, the
  // drawer entering/leaving, etc.) so the dropdown stays aligned. Without
  // this, the width read at first render becomes stale on resize.
  useLayoutEffect(() => {
    if (!anchorEl) {
      setAnchorWidth(undefined);
      return;
    }
    setAnchorWidth(anchorEl.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next != null) setAnchorWidth(next);
    });
    ro.observe(anchorEl);
    return () => ro.disconnect();
  }, [anchorEl]);

  const filteredUsers = useMemo<CommentUser[]>(() => {
    if (!open) return [];
    const trimmedQuery = query.trim().toLowerCase();
    const filtered = !trimmedQuery
      ? users
      : users.filter((u: CommentUser) =>
          fullName(u).toLowerCase().includes(trimmedQuery),
        );
    return filtered.slice(0, MAX_DROPDOWN_RESULTS);
  }, [query, users, open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [filteredUsers.length, open]);

  useEffect(() => {
    if (!open || filteredUsers.length === 0 || !anchorEl) return;

    const handleKeyDown = (e: Event) => {
      const ev = e as KeyboardEvent;
      if (ev.key === "ArrowDown") {
        setActiveIndex((i) => (i + 1) % filteredUsers.length);
        ev.preventDefault();
      } else if (ev.key === "ArrowUp") {
        setActiveIndex(
          (i) => (i - 1 + filteredUsers.length) % filteredUsers.length,
        );
        ev.preventDefault();
      } else if (ev.key === "Enter" || ev.key === "Tab") {
        onSelect(filteredUsers[activeIndex]);
        ev.preventDefault();
      } else if (ev.key === "Escape") {
        onClose?.();
        ev.preventDefault();
      }
    };

    // Scope to the anchor (the input row) so other focused inputs on the
    // page can't have their arrow / tab presses intercepted by this
    // dropdown.
    anchorEl.addEventListener("keydown", handleKeyDown);
    return () => anchorEl.removeEventListener("keydown", handleKeyDown);
  }, [open, filteredUsers, activeIndex, onSelect, onClose, anchorEl]);

  const shouldRender =
    open && !usersLoading && filteredUsers.length > 0 && !!anchorEl;

  return (
    <Popper
      open={shouldRender}
      anchorEl={anchorEl}
      // `flip` swaps to bottom-start when the top placement would overflow
      // the viewport. `offset` adds a small visual gap from the anchor.
      modifiers={[
        { name: "flip", enabled: true },
        { name: "offset", options: { offset: [0, 8] } },
        { name: "preventOverflow", options: { padding: 8 } },
      ]}
      sx={{
        zIndex: 1400,
        // Width comes from the ResizeObserver above so the dropdown follows
        // the input row as the viewport / drawer resize.
        width: anchorWidth ?? "auto",
      }}
    >
      <Box
        role="listbox"
        aria-label={t("User mention suggestions")}
        sx={{
          maxHeight: 220,
          overflowY: "auto",
          background: "var(--color-bg-elevated)",
          border: "var(--border-width-1) solid var(--color-border-strong)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-lg)",
          padding: "var(--space-2)",
          // Hide scrollbar: keep the surface clean while still letting the
          // list scroll if it exceeds the max height.
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {filteredUsers.map((user, idx) => {
          const isActive = idx === activeIndex;
          return (
            <Box
              key={user.id}
              role="option"
              aria-selected={isActive}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onSelect(user)}
              onMouseEnter={() => setActiveIndex(idx)}
              sx={{
                padding: "var(--space-2) var(--space-3)",
                borderRadius: "var(--radius-md)",
                cursor: "pointer",
                typography: "body1",
                color: "var(--color-text-primary)",
                background: isActive
                  ? "var(--color-bg-surface)"
                  : "transparent",
                transition: "background-color 120ms ease",
              }}
            >
              <Inline gap="var(--space-2)" align="center" wrap={false}>
                <UserAvatar size="sm" user={user} />
                {/* One flex item, so the gap does not split the name at the highlight. */}
                <span>{highlightMatch(fullName(user), query)}</span>
              </Inline>
            </Box>
          );
        })}
      </Box>
    </Popper>
  );
};

export default MentionsDropdown;
